import { z } from 'zod';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ModelName } from '@casops/store/api';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm from '@casops/components/layout/model-form/useModelForm';
import { ServerErrorFields } from '@dmc/ui';
import { ConfigurationGroupDeviceType } from '@casops/store/apis/commonModels';
import {
  ConfigurationGroupFormModel,
  ConfigurationGroupModel,
  CreateConfigurationGroupModel,
  EditConfigurationGroupModel,
  useCreateConfigurationGroupMutation,
  useEditConfigurationGroupMutation,
} from '@casops/store/apis/configurationGroupsApi';
import { ValidNameRegex } from '@casops/store/apis/commonModels';
import { useCompatibilityOptions } from '../../useCompatibilityOptions';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import { useFormEditMode } from '@casops/components/layout/model-form/useFormEditMode';

const DESCRIPTION_MAX = 255;
const NAME_MAX = 128;

type DataModel = ConfigurationGroupModel;
type FormModel = ConfigurationGroupFormModel;
type CreateModel = CreateConfigurationGroupModel;
type EditModel = EditConfigurationGroupModel;

const modelName = ModelName.ConfigurationGroup;

const defaultValues: FormModel = {
  name: '',
  hw: ConfigurationGroupDeviceType.Generic,
  desc: '',
};

const getEditArgs = ({ name, desc }: FormModel): EditModel => ({ name, desc });

const serverErrorFields: ServerErrorFields<FormModel> = {
  name: 'Name',
  hw: 'Device Type',
  desc: 'Description',
};

interface ConfigurationGroupFormProps {
  item?: DataModel;
}

export const ConfigurationGroupForm = ({ item }: ConfigurationGroupFormProps): ReactElement => {
  const { t } = useTranslation();

  const editMode = useFormEditMode();

  const schema = useMemo(() => {
    return z.object({
      name: z
        .string()
        .min(1, { message: t('Validation.SpecifyName') })
        .max(NAME_MAX, { message: t('Validation.NameTooLong', { max: NAME_MAX }) })
        .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
      hw: z.nativeEnum(ConfigurationGroupDeviceType, {
        errorMap: () => ({ message: t('Validation.SpecifyValidDeviceType') }),
      }),
      desc: z.string().max(DESCRIPTION_MAX, {
        message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
      }),
    });
  }, [t]);

  const [triggerCreateItem] = useCreateConfigurationGroupMutation();
  const [triggerEditItem] = useEditConfigurationGroupMutation();

  const { canSubmit, onSubmit, errorMessage, control } = useModelForm<
    DataModel,
    FormModel,
    CreateModel,
    EditModel
  >({
    modelName,
    defaultValues,
    item,
    schema,
    triggerCreateItem,
    serverErrorFields,
    triggerEditItem,
    getEditArgs,
  });

  const compatibilityOptions = useCompatibilityOptions();

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
    >
      <>
        <CasFormInput key="name" control={control} name="name" label={t('Fields.Name')} />
        <CasFormDropdown
          key={'hw'}
          control={control}
          name="hw"
          label={t('Fields.Compatibility')}
          items={compatibilityOptions}
          disabled={editMode}
        />
        <CasFormInput
          key={'desc'}
          control={control}
          name="desc"
          label={t('Fields.Description')}
          element="textarea"
        />
      </>
    </ModelForm>
  );
};

export default ConfigurationGroupForm;
